/**
 * What counts as a slash command in a chat message.
 *
 * Shared because two layers have to agree on it: the web composer, which
 * decides whether the app handles the command itself, and the turn dispatcher,
 * which decides whether the text may be wrapped in context blocks before it
 * reaches the provider. A message the composer forwards as a command and the
 * dispatcher treats as prose is the exact mismatch that broke `/compact`: the
 * Claude CLI only recognises a slash command when it is the whole message, so
 * prepending anything to it turns the command into a question the agent answers
 * in prose.
 */

/**
 * The command name space: a leading letter, then letters, digits, `:`, `_` or
 * `-`. The colon is what lets a plugin- or skill-qualified name (`plugin:skill`)
 * be recognised as one command rather than as an unknown one.
 *
 * The separator and the arguments both exclude line breaks, so an invocation is
 * a single line by construction. A message that opens with a command and then
 * keeps talking is prose: the providers read it that way, and treating it as a
 * command here would be the same mismatch this module exists to prevent.
 */
const SLASH_COMMAND_PATTERN = /^\/([a-z][a-z0-9:_-]*)(?:[^\S\r\n]+([^\r\n]*))?$/i;

export interface SlashCommandInvocation {
  readonly command: string;
  readonly args: string;
}

export function normalizeSlashCommandName(value: string): string {
  return value.trim().replace(/^\/+/, "").toLowerCase();
}

/**
 * Splits a message into a slash command name and its arguments, without
 * checking the name against any catalog — whose command it is, is the caller's
 * question.
 *
 * Returns `null` for anything that is not *entirely* a command: the pattern is
 * anchored at both ends and `.` does not span newlines, so a message that only
 * happens to start with a slash stays prose.
 */
export function parseSlashCommandInvocation(text: string): SlashCommandInvocation | null {
  const match = SLASH_COMMAND_PATTERN.exec(text.trim());
  if (!match) {
    return null;
  }
  const command = normalizeSlashCommandName(match[1] ?? "");
  if (!command) {
    return null;
  }
  return { command, args: (match[2] ?? "").trim() };
}

/** True when the whole message is a slash command invocation. */
export function isSlashCommandMessage(text: string): boolean {
  return parseSlashCommandInvocation(text) !== null;
}
